import React, { useContext, useEffect, useState } from 'react';
import { PeFilterContext } from '../../Contexts/PeFilterContext';
import Popup from '../Common/Popup';

/**
 * The ReturnMultipleFilter function is a React component that renders a form input for selecting a
 * range of return multiples.
 * @returns a JSX element, which represents a form input for filtering return multiples.
 */
function ReturnMultipleFilter({ customPathname, ReturnMultiple, setReturnMultiple, isPopupOpen, setIsPopupOpen }) {
    const { refineSearchFilters } = useContext(PeFilterContext);
    const [internalPopupOpen, setInternalPopupOpen] = useState(false);
    const [error, setError] = useState("");
    
    // Use centralized state if provided, otherwise fall back to internal state
    const popupOpen = isPopupOpen !== undefined ? isPopupOpen : internalPopupOpen;
    const setPopupOpen = setIsPopupOpen !== undefined ? setIsPopupOpen : setInternalPopupOpen;

    useEffect(() => {
        const from = refineSearchFilters[customPathname]?.returnMultiple?.at(0)?.name ?? "";
        const to = refineSearchFilters[customPathname]?.returnMultiple?.at(1)?.name ?? "";
        setReturnMultiple({ from, to })
    }, [customPathname, refineSearchFilters, setReturnMultiple]);

    const handleChange = (field, value) => {
        setReturnMultiple(prev => ({ ...prev, [field]: value }));
        // Clear error when user starts typing
        if (error) setError("");
    };

    // Validate when one field has value but other is empty
    useEffect(() => {
        const hasFrom = ReturnMultiple.from !== "" && ReturnMultiple.from !== null;
        const hasTo = ReturnMultiple.to !== "" && ReturnMultiple.to !== null;
        
        if (hasFrom && !hasTo) {
            setError("Please enter Max Value");
        } else if (!hasFrom && hasTo) {
            setError("Please enter Min Value");
        } else if (hasFrom && hasTo && parseFloat(ReturnMultiple.from) > parseFloat(ReturnMultiple.to)) {
            setError("Min Value must be less than Max Value");
        } else {
            setError("");
        }
    }, [ReturnMultiple.from, ReturnMultiple.to]);

    const hasValue = ReturnMultiple.from !== "" || ReturnMultiple.to !== "";

    return (
        <div className="relative w-full md:inline-block">
            <button
                type="button"
                className="h-10 rounded-md px-4 py-1 text-gray-500 hover:bg-gray-100 text-sm shadow-md border border-gray-300 text-left flex items-center justify-between w-full md:w-[90%]"
                onClick={() => setPopupOpen(!popupOpen)}
            >
                <span className="truncate pr-2">Return Multiple</span>
                <div className="flex items-center gap-2 flex-shrink-0">
                    {hasValue && (
                        <span className="bg-white rounded-full px-2 py-0.5 text-xs font-medium border border-gray-400">
                            {ReturnMultiple.from && ReturnMultiple.to 
                                ? `${ReturnMultiple.from}x - ${ReturnMultiple.to}x`
                                : ReturnMultiple.from 
                                    ? `≥ ${ReturnMultiple.from}x` 
                                    : `≤ ${ReturnMultiple.to}x`}
                        </span>
                    )}
                    <span className={`transition-transform duration-200 text-xl font-semibold ${popupOpen ? 'rotate-[270deg]' : 'rotate-90'}`}>
                        ›
                    </span>
                </div>
            </button>

            {popupOpen && (
                <Popup onClose={() => setPopupOpen(false)}>
                    <div className="p-4 min-w-[225px] md:min-w-[300px]">
                        <label className="block text-sm font-medium text-gray-700 mb-3">
                            Return Multiple Range
                        </label>
                        <div className="space-y-3">
                            <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">
                                    Min Value (x)
                                </label>
                                <input
                                    type="number"
                                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-1 ${
                                        error && (!ReturnMultiple.from || parseFloat(ReturnMultiple.from) > parseFloat(ReturnMultiple.to))
                                            ? 'border-red-500 focus:ring-red-500'
                                            : 'border-gray-300 focus:ring-[#BC8B20]'
                                    }`}
                                    value={ReturnMultiple.from}
                                    onChange={(e) => handleChange('from', e.target.value)}
                                    min="0"
                                    step="0.1"
                                    placeholder="Enter minimum value"
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">
                                    Max Value (x)
                                </label>
                                <input
                                    type="number"
                                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-1 ${
                                        error && (!ReturnMultiple.to || parseFloat(ReturnMultiple.from) > parseFloat(ReturnMultiple.to))
                                            ? 'border-red-500 focus:ring-red-500'
                                            : 'border-gray-300 focus:ring-[#BC8B20]'
                                    }`}
                                    value={ReturnMultiple.to}
                                    onChange={(e) => handleChange('to', e.target.value)}
                                    min="0"
                                    step="0.1"
                                    placeholder="Enter maximum value"
                                />
                            </div>
                            {error && (
                                <div className="text-red-600 text-xs font-medium mt-2">
                                    {error}
                                </div>
                            )}
                        </div>
                    </div>
                </Popup>
            )}
        </div>
    );
}

export default ReturnMultipleFilter;